import {
  type ApiClient,
  type CallArgs,
  type Endpoint,
  type EndpointResponse,
  internal,
} from '@croissants/contracts';
import { queryKeyFor } from '@croissants/ui';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useApi } from './api';

/**
 * The frame's start-up reads, as ONE request (`GET /internal/shell`).
 *
 * Every screen opens under the rail's Support badge (`/internal/support/counts`),
 * most of them on the platform's day (`/internal/time`, `usePlatformDay`) and
 * the landing one on the ordering switches (`/internal/ordering`). Fired apart,
 * those were up to three calls beside the screen's own on every first load,
 * and each call is another chance of landing on a cold Worker isolate that
 * waits 0.5-1.2 s before its handler runs (demo, 24 Oct 2026).
 *
 * So the FIRST fetch of each of the three cache entries is the shell's answer,
 * cut into its three parts. The entries are the readers' own - the key is
 * `queryKeyFor`, as `useEndpointQuery` builds it - and they are started before
 * any reader subscribes, so a reader mounting a moment later joins the fetch
 * already in flight instead of starting its own. Only that first fetch is the
 * shell's: every later one (the badge's 15 s poll, the clock's 30 s, an
 * invalidation after a write) runs the reader's own call to the old endpoint.
 *
 * If the shell call fails, each part falls back to its own endpoint, so one
 * failing read still fails alone - the rail must never go down with a panel.
 *
 * Once per query client: a remount, or a second render of the frame, starts
 * nothing.
 */
type Shell = EndpointResponse<typeof internal.getShell>;

const started = new WeakSet<QueryClient>();

export function startShellRead(queryClient: QueryClient, client: ApiClient): void {
  if (started.has(queryClient)) return;
  started.add(queryClient);

  /* Shared by the three parts and never aborted by one of them: a reader that
     unmounts must not cancel the answer the other two are waiting on. */
  let shell: Promise<Shell> | undefined;
  const shellOnce = (): Promise<Shell> => {
    shell ??= client.call(internal.getShell, {});
    return shell;
  };

  function part<E extends Endpoint>(endpoint: E, pick: (answer: Shell) => EndpointResponse<E>) {
    void queryClient.prefetchQuery({
      queryKey: queryKeyFor(endpoint, {}),
      queryFn: async ({ signal }) => {
        let answer: Shell;
        try {
          answer = await shellOnce();
        } catch {
          return client.call(endpoint, { signal } as CallArgs<E>);
        }
        return pick(answer);
      },
    });
  }

  part(internal.getPlatformTime, (answer) => answer.time);
  part(internal.getOrderingModes, (answer) => answer.ordering);
  part(internal.getSupportCounts, (answer) => answer.supportCounts);
}

/**
 * Called at the top of the frame's render, before any reader under it exists,
 * so the three entries are already fetching when the first reader subscribes.
 */
export function useShellRead(): void {
  const queryClient = useQueryClient();
  const client = useApi().client;
  startShellRead(queryClient, client);
}
